import {
  Button,
  Heading,
  Icon,
  Input,
  Link,
  Select,
  Switch,
  Text,
  useToastContext,
} from "@ttc3k/trekker";
import { Box, Flex } from "styled-system/jsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  EnumOperatorEnrollmentsOnboardingStatus,
  OperatorByIdDocument,
  OperatorByIdQuery,
  useOperatorAddExistingToOrganizationMutation,
  useOperatorUpdateMutation,
  useOrganizationManyQuery,
} from "gql/generated";
import { getMultilingualString } from "shared/utils";
import { BackOfficeTags, CloudinaryImage } from "shared/components";
import { LinkSlash, OpenNewWindow, Plus } from "iconoir-react";

type Operator = NonNullable<OperatorByIdQuery["operatorById"]>;
type Enrollment = NonNullable<NonNullable<Operator["enrollments"]>[number]>;

type EnrollmentState = {
  organizationId: string;
  organizationName: string;
  organizationLogo: NonNullable<Enrollment["organization"]>["logo"];
  organizationControlled: boolean;
  onboardingStatus: EnumOperatorEnrollmentsOnboardingStatus;
  disconnected: boolean;
};

type AdminlandBackOfficeContentProps = {
  operator: Operator;
  operatorEntityId: string;
  onOperatorUpdated?: (updatedOperator: Partial<Operator>) => void;
  refetchOperator: () => void;
  onMarkDeletedClick?: () => void;
  onDeleteCompletelyClick?: () => void;
};

const ONBOARDING_STATUS_OPTIONS = [
  {
    value: EnumOperatorEnrollmentsOnboardingStatus.CompletedOnboarding,
    label: "Connected",
  },
  {
    value: EnumOperatorEnrollmentsOnboardingStatus.CompletedSurvey,
    label: "Completed Survey",
  },
  {
    value: EnumOperatorEnrollmentsOnboardingStatus.Incomplete,
    label: "Pending",
  },
] as const;

export const AdminlandBackOfficeContent = ({
  operator,
  operatorEntityId,
  onOperatorUpdated,
  refetchOperator,
  onMarkDeletedClick,
  onDeleteCompletelyClick,
}: AdminlandBackOfficeContentProps) => {
  const { t } = useTranslation("adminland", {
    keyPrefix: "PAGE.LISTINGS.ADMINLAND_BACKOFFICE",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();

  const [updateOperator, { loading: isSaving }] = useOperatorUpdateMutation({
    refetchQueries: [OperatorByIdDocument],
  });
  const [addToOrg] = useOperatorAddExistingToOrganizationMutation();
  const { data: orgData } = useOrganizationManyQuery({});

  const [isOrgPickerOpen, setIsOrgPickerOpen] = useState(false);
  const [orgSearch, setOrgSearch] = useState("");
  const [connectingOrgId, setConnectingOrgId] = useState<string | null>(null);

  /** Stable when server enrollment *data* is unchanged; avoids reset on Apollo ref churn. */
  const enrollmentSyncSignature = useMemo(
    () =>
      JSON.stringify(
        (operator.enrollments ?? [])
          .filter((e): e is Enrollment => !!e?.organization)
          .map(e => ({
            id: e.organization!._id,
            organizationControlled: e.organizationControlled,
            onboardingStatus: e.onboardingStatus,
          })),
      ),
    [operator.enrollments],
  );

  const initialEnrollments = useMemo<EnrollmentState[]>(() => {
    return (operator.enrollments ?? [])
      .filter((e): e is Enrollment => !!e?.organization)
      .map(e => ({
        organizationId: e.organization!._id,
        organizationName: getMultilingualString(e.organization!.name),
        organizationLogo: e.organization!.logo,
        organizationControlled: e.organizationControlled,
        onboardingStatus: e.onboardingStatus,
        disconnected: false,
      }));
  }, [enrollmentSyncSignature]);

  const [enrollments, setEnrollments] =
    useState<EnrollmentState[]>(initialEnrollments);

  useEffect(() => {
    setEnrollments(initialEnrollments);
  }, [initialEnrollments]);

  const isDirty = useMemo(() => {
    const byOrgId = (list: EnrollmentState[]) =>
      new Map(list.map(e => [String(e.organizationId), e]));
    const initById = byOrgId(initialEnrollments);
    const curById = byOrgId(enrollments);
    if (initById.size !== curById.size) return true;
    for (const [id, e] of curById) {
      const orig = initById.get(id);
      if (!orig) return true;
      if (e.disconnected) return true;
      if (
        e.organizationControlled !== orig.organizationControlled ||
        e.onboardingStatus !== orig.onboardingStatus
      ) {
        return true;
      }
    }
    return false;
  }, [enrollments, initialEnrollments]);

  const updateEnrollment = useCallback(
    (orgId: string, patch: Partial<EnrollmentState>) => {
      setEnrollments(prev =>
        prev.map(e =>
          e.organizationId === orgId ? { ...e, ...patch } : e,
        ),
      );
    },
    [],
  );

  const statusCollection = Select.createCollection({
    items: ONBOARDING_STATUS_OPTIONS.map(o => ({
      value: o.value,
      label: t(`STATUS.${o.value}`, { defaultValue: o.label }),
    })),
  });

  const handleSave = async () => {
    const activeEnrollments = enrollments.filter(e => !e.disconnected);
    try {
      await updateOperator({
        variables: {
          entityId: operatorEntityId,
          record: {
            enrollments: activeEnrollments.map(e => ({
              organization: e.organizationId,
              organizationControlled: e.organizationControlled,
              onboardingStatus: e.onboardingStatus,
            })),
          },
        },
      });
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: t("SAVE_SUCCESS"),
      });
      refetchOperator();
      onOperatorUpdated?.({
        _entityId: operatorEntityId,
        enrollments: activeEnrollments,
      });
    } catch {
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("SAVE_ERROR"),
      });
    }
  };

  const enrolledOrgIds = useMemo(
    () => new Set(enrollments.map(e => e.organizationId)),
    [enrollments],
  );

  const availableOrgs = useMemo(() => {
    const allOrgs = orgData?.organizationMany ?? [];
    const notEnrolled = allOrgs.filter(o => !enrolledOrgIds.has(o._id));
    if (!orgSearch.trim()) return notEnrolled;
    const q = orgSearch.toLowerCase();
    return notEnrolled.filter(o =>
      getMultilingualString(o.name).toLowerCase().includes(q),
    );
  }, [orgData, enrolledOrgIds, orgSearch]);

  const handleConnect = async (organizationId: string) => {
    setConnectingOrgId(organizationId);
    try {
      await addToOrg({
        variables: { organizationId, operatorEntityId },
      });
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: t("CONNECT_SUCCESS"),
      });
      refetchOperator();
      setIsOrgPickerOpen(false);
      setOrgSearch("");
    } catch {
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("CONNECT_ERROR"),
      });
    } finally {
      setConnectingOrgId(null);
    }
  };

  const visibleEnrollments = enrollments.filter(e => !e.disconnected);
  const hasDisconnected = enrollments.some(e => e.disconnected);

  const orgPickerUI = (
    <>
      <Button
        visual="primary"
        mode="light"
        size="sm"
        Icon={Plus}
        onClick={() => setIsOrgPickerOpen(prev => !prev)}
        type="button"
      >
        {t("CONNECT_TO_ORG")}
      </Button>

      {isOrgPickerOpen && (
        <Flex
          flexDir="column"
          gap="200"
          borderWidth="1px"
          borderColor="border.light"
          borderRadius="100"
          p="300"
        >
          <Input
            placeholder={t("CONNECT_SEARCH_PLACEHOLDER")}
            value={orgSearch}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setOrgSearch(e.target.value)
            }
          />

          <Flex flexDir="column" gap="100" maxH="240px" overflow="auto">
            {availableOrgs.length === 0 ? (
              <Text visual="smallRegular" color="text.light" p="200">
                {t("CONNECT_NO_ORGS")}
              </Text>
            ) : (
              availableOrgs.map(org => (
                <Flex
                  key={org._id}
                  alignItems="center"
                  justifyContent="space-between"
                  gap="200"
                  p="200"
                  borderRadius="100"
                  _hover={{ bg: "bg.wa.light" }}
                  cursor="pointer"
                >
                  <Flex alignItems="center" gap="200" minW={0} flex={1}>
                    {org.logo && (
                      <Box w="24px" h="24px" flexShrink={0}>
                        <CloudinaryImage
                          image={org.logo}
                          imageStyleProps={{
                            objectFit: "contain",
                            w: "24px",
                            h: "24px",
                          }}
                        />
                      </Box>
                    )}
                    <Text
                      visual="smallRegular"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {getMultilingualString(org.name)}
                    </Text>
                  </Flex>
                  <Button
                    visual="accent.blue"
                    mode="dark"
                    size="sm"
                    onClick={() => handleConnect(org._id)}
                    disabled={connectingOrgId !== null}
                    isLoading={connectingOrgId === org._id}
                    type="button"
                  >
                    {t("CONNECT")}
                  </Button>
                </Flex>
              ))
            )}
          </Flex>
        </Flex>
      )}
    </>
  );

  const dangerZone =
    onMarkDeletedClick || onDeleteCompletelyClick ? (
      <Flex flexDir="column" gap="200">
        <Text visual="bodySemiBold">{t("DANGER_ZONE")}</Text>
        <Box borderTopWidth="1px" borderColor="border.light" pt="200" />
        <Flex flexDir="column" gap="150" alignItems="flex-start">
          {onMarkDeletedClick && (
            <Button
              visual="warning"
              mode="light"
              size="sm"
              onClick={onMarkDeletedClick}
              disabled={!operator.enrollments?.length}
              type="button"
            >
              {t("MARK_AS_DELETED")}
            </Button>
          )}
          {onDeleteCompletelyClick && (
            <Button
              visual="destructive"
              mode="light"
              size="sm"
              onClick={onDeleteCompletelyClick}
              type="button"
            >
              {t("DELETE_PROFILE_COMPLETELY")}
            </Button>
          )}
        </Flex>
      </Flex>
    ) : null;

  if (!operator.enrollments?.length) {
    return (
      <Flex flexDir="column" p="600" gap="300">
        <Heading size="h5">{t("TITLE")}</Heading>
        <Text color="text.light">{t("EMPTY")}</Text>
        {orgPickerUI}
        {dangerZone}
      </Flex>
    );
  }

  return (
    <Flex flexDir="column" p="600" gap="500">
      <Heading size="h5">{t("TITLE")}</Heading>

      {visibleEnrollments.map(enrollment => (
        <Flex
          key={enrollment.organizationId}
          flexDir="column"
          gap="300"
          borderWidth="1px"
          borderColor="border.light"
          borderRadius="100"
          p="400"
        >
          {/* Org identity */}
          <Flex alignItems="center" gap="200">
            {enrollment.organizationLogo && (
              <Box w="32px" h="32px" flexShrink={0}>
                <CloudinaryImage
                  image={enrollment.organizationLogo!}
                  imageStyleProps={{
                    objectFit: "contain",
                    w: "32px",
                    h: "32px",
                  }}
                />
              </Box>
            )}
            <Text visual="bodySemiBold">
              {enrollment.organizationName}
            </Text>
          </Flex>

          {/* Back office tags (read-only) */}
          <Flex flexDir="column" gap="100">
            <Text visual="smallSemiBold" color="text.light">
              {t("TAGS_LABEL")}
            </Text>
            <BackOfficeTags
              operator={operator}
              operatorEntityId={operatorEntityId}
              organizationId={enrollment.organizationId}
              compact
              readOnly
            />
          </Flex>

          {/* DMO Managed toggle */}
          <Flex alignItems="center" justifyContent="space-between" gap="200">
            <Text visual="bodyRegular">{t("DMO_MANAGED")}</Text>
            <Switch
              checked={enrollment.organizationControlled}
              onCheckedChange={e =>
                updateEnrollment(enrollment.organizationId, {
                  organizationControlled: e.checked,
                })
              }
            />
          </Flex>

          {/* Onboarding status */}
          <Flex flexDir="column" gap="100">
            <Text visual="smallSemiBold" color="text.light">
              {t("CONNECTION_STATUS")}
            </Text>
            <Select.Root
              collection={statusCollection}
              value={[enrollment.onboardingStatus]}
              onValueChange={({ value }: { value: string[] }) =>
                updateEnrollment(enrollment.organizationId, {
                  onboardingStatus:
                    value[0] as EnumOperatorEnrollmentsOnboardingStatus,
                })
              }
              size="md"
            >
              {statusCollection.items.map(opt => (
                <Select.Item key={opt.value} item={opt} />
              ))}
            </Select.Root>
          </Flex>

          {/* Actions */}
          <Flex gap="200" alignItems="center" pt="100">
            <Link
              href={`/app/organization/${enrollment.organizationId}/directory/${operatorEntityId}`}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              display="inline-flex"
              alignItems="center"
              gap="100"
              color="text.accent.blue.mid"
              fontWeight="500"
              textDecoration="none"
              _hover={{ textDecoration: "underline" }}
            >
              <Icon Element={OpenNewWindow} w="16px" h="16px" flexShrink={0} />
              {t("EDIT_LISTING")}
            </Link>
            <Button
              visual="destructive"
              mode="light"
              size="sm"
              Icon={LinkSlash}
              onClick={() =>
                updateEnrollment(enrollment.organizationId, {
                  disconnected: true,
                })
              }
              type="button"
            >
              {t("DISCONNECT")}
            </Button>
          </Flex>
        </Flex>
      ))}

      {orgPickerUI}

      {hasDisconnected && (
        <Text visual="smallRegular" color="text.warning.mid">
          {t("PENDING_DISCONNECTIONS", {
            count: enrollments.filter(e => e.disconnected).length,
          })}
        </Text>
      )}

      {(hasDisconnected || isDirty) && (
        <Flex justifyContent="flex-end" pt="100">
          <Button
            visual="accent.blue"
            mode="dark"
            onClick={handleSave}
            disabled={isSaving}
            isLoading={isSaving}
            type="button"
          >
            {t("SAVE")}
          </Button>
        </Flex>
      )}

      {dangerZone}
    </Flex>
  );
};
